// Bug de producción 2026-08-26 (hoja "Distribución Fundamental" del export de Brillamos Juntos) —
// la plantilla de referencia del cliente acompaña la tabla con una gráfica de barras horizontales
// ("Reconocimientos por Fundamental / Behavior"). `exceljs` no escribe gráficas nativas de Excel
// (ver `ExcelImageHint` en `exportExcelMultiSheet.ts`): la única vía es insertar una IMAGEN, así que
// esta función rasteriza la gráfica con Chart.js sobre un canvas fuera de pantalla.
import {
  BarController, BarElement, CategoryScale, Chart, LinearScale, Title,
} from 'chart.js';

Chart.register(BarController, BarElement, CategoryScale, LinearScale, Title);

/** Una barra de la gráfica: etiqueta del eje, valor numérico y SU PROPIO color. */
export interface BarChartDatum {
  etiqueta: string;
  valor: number;
  /** Color CSS de la barra (ej. `#3B82F6`) — el del Fundamental guardado en catálogo, no uno único
   *  inventado para el export (fix 26-ago-2026, hallazgo visual: gráfica salía toda negra/gruesa). */
  color: string;
}

const ANCHO_PX = 720;
/** Alto por categoría y piso mínimo del canvas: con pocos Fundamentales, un alto fijo dejaba cada
 *  barra ocupando toda su fila (gruesa y poco estética); con muchos, los apretaba. Escalar por
 *  categoría mantiene el mismo grosor de barra sin importar cuántas haya. */
const ALTO_POR_CATEGORIA_PX = 46;
const ALTO_MINIMO_PX = 220;
const ALTO_RESERVADO_TITULO_Y_EJE_PX = 90;
/** Tope de grosor de barra en píxeles — el fix real contra "se ve burdo": sin esto, Chart.js estira
 *  cada barra para llenar el alto de su categoría, y con pocas categorías eso las vuelve gigantes. */
const GROSOR_MAXIMO_BARRA_PX = 28;

/**
 * Renderiza una gráfica de barras HORIZONTALES a PNG (base64, sin el prefijo `data:image/...`) y
 * devuelve también sus dimensiones reales — `exceljs` necesita el alto/ancho para no deformar la
 * imagen al insertarla (`ExcelImageHint.range`).
 *
 * `animation: false` + `responsive: false`: sin esto, Chart.js intenta observar el tamaño de un
 * contenedor padre (que no existe, el canvas nunca se adjunta al DOM) y difiere el primer dibujo a
 * un frame de animación — con animación desactivada dibuja de forma síncrona en el constructor, así
 * que `toBase64Image()` ya tiene contenido real justo después.
 */
export function renderBarChartPng(
  titulo: string,
  datos: readonly BarChartDatum[],
): { base64: string; width: number; height: number } {
  const alto = Math.max(ALTO_MINIMO_PX, datos.length * ALTO_POR_CATEGORIA_PX + ALTO_RESERVADO_TITULO_Y_EJE_PX);

  const canvas = document.createElement('canvas');
  canvas.width = ANCHO_PX;
  canvas.height = alto;

  const chart = new Chart(canvas, {
    type: 'bar',
    data: {
      labels: datos.map((dato) => dato.etiqueta),
      datasets: [{
        data: datos.map((dato) => dato.valor),
        backgroundColor: datos.map((dato) => dato.color),
        borderRadius: 4,
        maxBarThickness: GROSOR_MAXIMO_BARRA_PX,
        categoryPercentage: 0.7,
        barPercentage: 0.8,
      }],
    },
    options: {
      indexAxis: 'y',
      animation: false,
      responsive: false,
      devicePixelRatio: 1,
      plugins: {
        title: { display: true, text: titulo, font: { size: 16 } },
        legend: { display: false },
      },
      scales: {
        x: { beginAtZero: true, ticks: { precision: 0 } },
      },
    },
  });

  const base64 = chart.toBase64Image();
  chart.destroy();

  return { base64, width: ANCHO_PX, height: alto };
}
